import type React from 'react';
import logoWhite from '../assets/logo_white.png';
import logoBlack from '../assets/logo_black.png';
import iconWhite from '../assets/icon_white.png';
import iconBlack from '../assets/icon_black.png';
import logoSvg from '../assets/logo.svg';
import { APP_NAME } from '../constants/branding';

export type LogoVariant = 'full' | 'icon';
export type LogoTheme = 'light' | 'dark';

export interface LogoProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  /** 'full' for the wordmark, 'icon' for the compact icon */
  variant?: LogoVariant;
  /** 'light' renders the dark logo (for light backgrounds), 'dark' renders the white logo (for dark backgrounds) */
  theme?: LogoTheme;
  /**
   * Lift the logo so it reads as centred beside text (default true; see
   * OPTICAL_OFFSET). Turn it off where the logo is pinned to an edge rather
   * than centred in a row.
   */
  optical?: boolean;
}

/**
 * How far to lift each asset, as a percentage of its own height.
 *
 * Full logo: line the LETTERING up with text beside it, ignoring the mark. The
 * mark is taller than the letters, so centring the image centred the mark and
 * left "Showliner" low against nav text. Two measured parts: the letters'
 * centre sits 3.6% of the height below the image's middle (the files are
 * cropped to their ink, so that middle is the box's), and nav text in Rubik
 * draws its capitals ~1px above the centre of its own line box at 13px. Lifting
 * 10.9% of a 20px logo puts the letters' centre within a device pixel of the
 * neighbouring name's capitals (measured from pixels in the admin header, both
 * modes). Weight-centring (8.4%) was tried first and still read low.
 *
 * Mark alone: no lettering to align, so it is centred by its visual weight,
 * which the wave under it carries 4% low.
 *
 * Re-measure if the artwork or the header's type changes.
 */
const OPTICAL_OFFSET: Record<LogoVariant, number> = {
  full: 10.9,
  icon: 4,
};

/** Map of variant + theme to the correct asset */
const logoMap: Record<LogoVariant, Record<LogoTheme, string>> = {
  full: { light: logoBlack, dark: logoWhite },
  icon: { light: iconBlack, dark: iconWhite },
};

/**
 * Framework-agnostic Logo component.
 *
 * Renders a plain <img> so it works in MUI, Tailwind, or unstyled contexts.
 * Pass `className` or `style` to customise sizing.
 */
const Logo: React.FC<LogoProps> = ({
  variant = 'full',
  theme = 'light',
  alt = APP_NAME,
  optical = true,
  style,
  ...rest
}) => {
  const src = logoMap[variant][theme];
  // A percentage of the logo's own height, so the correction holds at any
  // size. `translate` rather than `transform` leaves a caller's transform alone,
  // and a caller's own `translate` still wins.
  const lift = optical ? { translate: `0 -${OPTICAL_OFFSET[variant]}%` } : undefined;
  return <img src={src} alt={alt} style={{ ...lift, ...style }} {...rest} />;
};

export default Logo;

// Re-export individual assets for cases that need direct access
export { logoWhite, logoBlack, iconWhite, iconBlack, logoSvg };
